import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import './i18n/index';
import './index.css';
import { onCLS, onFID, onLCP, onINP, onTTFB } from 'web-vitals';

// Initialize Sentry
if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: ['localhost', /^https:\/\/yourdomain\.com/],
      }),
      new Sentry.Replay(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Initialize React Query with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      retry: 3,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      suspense: true
    },
    mutations: {
      retry: 2,
      onError: error => {
        if (process.env.NODE_ENV === 'production') {
          Sentry.captureException(error);
        }
      }
    }
  }
});

// Register service worker with enhanced offline support
const updateSW = registerSW({
  onNeedRefresh() {
    if (confirm('New content available. Reload?')) {
      updateSW(true);
    }
  },
  onOfflineReady() {
    const event = new CustomEvent('app-ready-offline');
    window.dispatchEvent(event);
  },
  onRegisteredSW(swUrl, registration) {
    registration?.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          const event = new CustomEvent('app-update-available');
          window.dispatchEvent(event);
        }
      });
    });

    // Check for updates every hour
    setInterval(() => {
      registration?.update();
    }, 60 * 60 * 1000);
  }
});

// Enhanced web vitals reporting
function reportWebVitals(onPerfEntry?: (metric: any) => void) {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    onCLS(onPerfEntry);
    onFID(onPerfEntry);
    onLCP(onPerfEntry);
    onINP(onPerfEntry);
    onTTFB(onPerfEntry);
  }
}

// Error fallback component with retry functionality
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => (
  <div className="min-h-screen bg-cream flex items-center justify-center p-8">
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
      <h2 className="text-xl font-semibold text-red-600 mb-4">Something went wrong</h2>
      <pre className="text-sm text-red-500 mb-4 overflow-auto">{error.message}</pre>
      <button
        onClick={resetErrorBoundary}
        className="bg-gold text-white px-4 py-2 rounded-lg hover:bg-gold-dark transition-colors"
      >
        Try again
      </button>
    </div>
  </div>
);

// Enhanced initialization function with error boundaries and performance monitoring
function initConfigurator(containerId: string = 'root') {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with id "${containerId}" not found`);
    return;
  }

  try {
    const root = createRoot(container);
    root.render(
      <StrictMode>
        <ErrorBoundary 
          FallbackComponent={ErrorFallback}
          onReset={() => {
            queryClient.clear();
            window.location.reload();
          }}
          onError={(error) => {
            if (process.env.NODE_ENV === 'production') {
              Sentry.captureException(error);
            } else {
              console.error('Application Error:', error);
            }
          }}
        >
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </ErrorBoundary>
      </StrictMode>
    );

    // Report performance metrics in production
    if (process.env.NODE_ENV === 'production') {
      reportWebVitals((metric) => {
        // Type assertion for gtag
        const gtag = (window as any).gtag;
        if (gtag) {
          gtag('event', metric.name, {
            value: Math.round(metric.value * 1000) / 1000,
            event_category: 'Web Vitals',
            event_label: metric.id,
            non_interaction: true,
          });
        }
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error);
    }
    console.error('Failed to initialize configurator:', error);
  }
}

// Check for WordPress environment
if ((window as any).wp) {
  window.addEventListener('DOMContentLoaded', () => {
    initConfigurator('stone-configurator');
  });
} else {
  initConfigurator();
}

// Export for WordPress usage
(window as any).initStoneConfigurator = initConfigurator;